/** @jsxImportSource @emotion/react */
import { usePhotoUriForLayoutSize } from '@kaiizen/kai-pro-api-react-state';
import { type PointerEvent, useState } from 'react';

import { formatPlaybackTimeSeconds } from '../scanVideoFormatLib';
import {
  getGroupSegmentPlayedFraction,
  type VideoGroupSegment,
} from './videoGroupTimelineLib';
import { DEFAULT_VIDEO_PLAYBACK_PANEL_STYLES, type VideoPlaybackPanelStyles } from './videoPlaybackPanelStyles';
import { timelineTrackDisabledStyle } from './videoPlaybackTimelineStyles';

export type VideoPlaybackTimelineDensity = 'minimal' | 'segment' | 'filmstrip';

type VideoPlaybackTimelineProps = {
  density: VideoPlaybackTimelineDensity;
  durationSeconds: number;
  currentTimeSeconds: number;
  segments: readonly VideoGroupSegment[];
  activeSegment?: VideoGroupSegment;
  /** Scope group opened in the toolbox — brightens that room's band and dims the rest. */
  highlightGroupId?: string;
  seekDisabled?: boolean;
  onSeek: (videoTimeSeconds: number) => void;
  onSeekToSegmentAnchor: (segment: VideoGroupSegment) => void;
  styles?: VideoPlaybackPanelStyles;
};

const toFraction = (seconds: number, durationSeconds: number) => durationSeconds > 0 ? Math.min(1, Math.max(0, seconds / durationSeconds)) : 0;

/** A segment's anchor on the filmstrip: its photo's thumbnail, or a tick until one resolves. */
const FilmstripAnchor = ({ segment, anchorFraction, styles }: {
  segment: VideoGroupSegment;
  anchorFraction: number;
  styles: VideoPlaybackPanelStyles;
}) => {
  // A 56 × 50px cover crop: a 4:3 photo's long side spans 50 × 4/3.
  const thumbUri = usePhotoUriForLayoutSize(segment.photoId, 67);

  return thumbUri
    ? (
      <img
        src={thumbUri}
        alt={segment.groupName}
        css={styles.timelineFilmstripThumb(anchorFraction)}
        data-testid={`video-playback-filmstrip-thumb-${segment.groupId}`}
      />
    )
    : (
      <div
        css={styles.timelineAnchorTick(anchorFraction)}
        data-testid={`video-playback-anchor-${segment.groupId}`}
      />
    );
};

/** When there is no group metadata, every density degrades to `minimal`. */
export const getEffectivePlaybackTimelineDensity = (
  density: VideoPlaybackTimelineDensity,
  segments: readonly VideoGroupSegment[],
): VideoPlaybackTimelineDensity => segments.length ? density : 'minimal';

/** Scrubber track for walkthrough playback; density controls group bands and anchor ticks. */
export const VideoPlaybackTimeline = ({
  density,
  durationSeconds,
  currentTimeSeconds,
  segments,
  activeSegment,
  highlightGroupId,
  seekDisabled = false,
  onSeek,
  onSeekToSegmentAnchor,
  styles = DEFAULT_VIDEO_PLAYBACK_PANEL_STYLES,
}: VideoPlaybackTimelineProps) => {
  const [hoveredGroupId, setHoveredGroupId] = useState<string>();
  const [trackElement, setTrackElement] = useState<HTMLDivElement | null>(null);
  const effectiveDensity = getEffectivePlaybackTimelineDensity(density, segments);
  const showGroupSegments = effectiveDensity === 'segment' || effectiveDensity === 'filmstrip';
  const showFilmstrip = effectiveDensity === 'filmstrip';
  const playheadFraction = toFraction(currentTimeSeconds, durationSeconds);
  const dimNonHighlightedGroups = Boolean(
    highlightGroupId && segments.some(segment => segment.groupId === highlightGroupId),
  );
  const hoveredGroup = segments.find(segment => segment.groupId === hoveredGroupId);

  const seekFromPointer = (event: PointerEvent<HTMLDivElement>) => {
    if (!trackElement || seekDisabled || durationSeconds <= 0) return;
    const { left, width } = trackElement.getBoundingClientRect();
    onSeek(Math.min(1, Math.max(0, (event.clientX - left) / width)) * durationSeconds);
  };

  const getHoveredLabelLeftPercent = () => {
    if (!hoveredGroup) return 50;
    const leftFraction = toFraction(hoveredGroup.startSeconds, durationSeconds);
    const widthFraction = toFraction(hoveredGroup.endSeconds, durationSeconds) - leftFraction;
    return (leftFraction + widthFraction / 2) * 100;
  };

  return (
    <div css={styles.timeline} data-testid={'video-playback-timeline'}>
      {hoveredGroup && (
        <span
          css={[styles.timelineGroupHoverLabel, { left: `${getHoveredLabelLeftPercent()}%` }]}
          data-testid={'video-playback-group-hover-label'}
        >
          {hoveredGroup.groupName}
        </span>
      )}
      <div
        ref={setTrackElement}
        css={[
          styles.timelineTrack(effectiveDensity),
          showGroupSegments && styles.timelineTrackGrouped,
          dimNonHighlightedGroups && styles.timelineTrackHasHighlightGroup,
          seekDisabled && timelineTrackDisabledStyle,
        ]}
        role={'slider'}
        aria-label={'Video timeline'}
        aria-valuemin={0}
        aria-valuemax={durationSeconds}
        aria-valuenow={currentTimeSeconds}
        aria-valuetext={activeSegment
          ? `${formatPlaybackTimeSeconds(currentTimeSeconds)} — ${activeSegment.groupName}`
          : formatPlaybackTimeSeconds(currentTimeSeconds)}
        aria-disabled={seekDisabled}
        onPointerDown={event => {
          event.currentTarget.setPointerCapture(event.pointerId);
          seekFromPointer(event);
        }}
        onPointerMove={event => {
          if (event.buttons) seekFromPointer(event);
        }}
      >
        {!showGroupSegments && <div css={styles.timelineMinimalPlayedFill(playheadFraction)} />}
        {showGroupSegments && segments.map(segment => {
          const leftFraction = toFraction(segment.startSeconds, durationSeconds);
          const widthFraction = toFraction(segment.endSeconds, durationSeconds) - leftFraction;
          const isHighlightedGroup = highlightGroupId === segment.groupId;
          const isHoveredGroup = hoveredGroupId === segment.groupId;
          const playedFraction = getGroupSegmentPlayedFraction(segment, currentTimeSeconds);

          return (
            <div
              key={segment.groupId}
              css={styles.timelineGroupSegment(
                leftFraction,
                widthFraction,
                isHighlightedGroup,
                dimNonHighlightedGroups,
                isHoveredGroup,
              )}
              data-testid={`video-playback-group-segment-${segment.groupId}`}
            >
              <span css={styles.timelineGroupSegmentFill(playedFraction)} />
            </div>
          );
        })}
        {showGroupSegments && segments.map(segment => {
          const leftFraction = toFraction(segment.startSeconds, durationSeconds);
          const widthFraction = toFraction(segment.endSeconds, durationSeconds) - leftFraction;
          return (
            <button
              key={`hit-${segment.groupId}`}
              type={'button'}
              css={styles.timelineGroupHitTarget(leftFraction, widthFraction)}
              title={`Jump to ${segment.groupName}`}
              aria-label={`Jump to ${segment.groupName}`}
              disabled={seekDisabled}
              onMouseEnter={() => setHoveredGroupId(segment.groupId)}
              onMouseLeave={() => setHoveredGroupId(undefined)}
              onFocus={() => setHoveredGroupId(segment.groupId)}
              onBlur={() => setHoveredGroupId(undefined)}
              onClick={event => {
                event.stopPropagation();
                onSeekToSegmentAnchor(segment);
              }}
            />
          );
        })}
        {showFilmstrip && segments.map(segment => (
          <FilmstripAnchor
            key={segment.groupId}
            segment={segment}
            anchorFraction={toFraction(segment.anchorSeconds, durationSeconds)}
            styles={styles}
          />
        ))}
        <div css={styles.timelinePlayheadKnob(playheadFraction)} data-testid={'video-playback-playhead'} />
      </div>
    </div>
  );
};
